// ItemDetail.js
import React, { useEffect, useState } from 'react';
import { localized } from '../i18n/translations';
import { useParams, Link } from 'react-router-dom';
import Quantity from '../components/Quantity';
import Footer from '../components/Footer';
import NotifyAvailable from "../components/NotifyAvailable";
import CustomImage from '../components/CustomImage';
import PriceDisplay from '../components/PriceDisplay';
import PriceDisplayWithCustomOption from '../components/PriceDisplayWithCustomOption';
import SEO from '../components/SEO';
import axios from 'axios';
import { applyCountryPrices } from '../utils/countryPricing';
import apiUrl from '../components/Url';
import imgUrl from '../components/urlImg';
import { pixelViewContent } from '../components/MetaPixel';
import { trackEvent, getPriceGroup } from '../utils/track';
import { getCustomOptionGroups, serializeCustomOptionSelections, pickLabel, pickGroupName } from '../components/customOptionUtils';
import { getItemLeadDays, formatLeadDates, getOptionExtraDays, getOptionBadge, getProductLeadDays } from '../components/deliveryEstimate';
import { getAccessoriesForProfile, getAccessoriesTotalPrice, getSecondPetPrice, getAccessoryDescription, getAccessoryLabel, getCustomAccessoryPrice, getValidCustomAccessories } from '../components/petAccessoryUtils';
import PetLoveMarquee from '../components/PetLoveMarquee';

// Illustration products (plate, jewelry dish): pets per piece, and the photo cap (3 per pet).
const MAX_ILLUSTRATION_PETS = 10;
const MAX_ILLUSTRATION_IMAGES = 10;

// Color palette for the "personalizovani privjesci sa vašim ljubimcem" product.
// `darkCheck` marks light swatches that need a dark checkmark for contrast.
const PENDANT_COLORS = [
  { key: 'Svijetlo roza',   hex: '#F9C5D5', darkCheck: true },
  { key: 'Tamnije roza',    hex: '#EC5C9D', darkCheck: false },
  { key: 'Crvena',          hex: '#DC2626', darkCheck: false },
  { key: 'Svijetlo plava',  hex: '#93C5FD', darkCheck: true },
  { key: 'Tamno plava',     hex: '#1E3A8A', darkCheck: false },
  { key: 'Narandžasta',     hex: '#F97316', darkCheck: false },
  { key: 'Žuta',            hex: '#FACC15', darkCheck: true },
  { key: 'Svijetlo zelena', hex: '#86EFAC', darkCheck: true },
  { key: 'Tamnije zelena',  hex: '#15803D', darkCheck: false },
  { key: 'Crna',            hex: '#1F2937', darkCheck: false },
  { key: 'Bijela',          hex: '#F9FAFB', darkCheck: true },
  { key: 'Smeđa',           hex: '#8B5E3C', darkCheck: false },
];

// Function to sanitize HTML and escape dangerous content
function sanitizeHtml(text) {
  if (!text) return '';
  
  // Create a temporary div element
  const div = document.createElement('div');
  div.textContent = text;
  
  // Get the escaped text and replace newlines with <br />
  return div.innerHTML.replace(/\n/g, '<br />');
}

function ItemDetail({ cartItems, productsData, ChangeQty, addToCart, currency, country, apiCountry, translate, categories }) {
  const { productPath } = useParams();
  const [product, setProduct] = useState(null);
  const [productId, setProductId] = useState(null);
  
  // Check for preview key from URL
  const urlParams = new URLSearchParams(window.location.search);
  const previewKey = urlParams.get('key') || localStorage.getItem('previewKey') || '';
  
  // Store preview key in localStorage if found in URL and trigger data refetch
  React.useEffect(() => {
    if (urlParams.get('key')) {
      localStorage.setItem('previewKey', urlParams.get('key'));
      // Trigger a refetch by dispatching an event that index.js listens to
      window.dispatchEvent(new CustomEvent('previewKeyDetected'));
    }
  }, []);
  const [currentQuantity, setCurrentQuantity] = useState(1);
  const [currectImgPath, setCurrentImgPath] = useState();
  const [showModal, setShowModal] = React.useState(false);
  const [notifyProductId, setNotifyProductId] = React.useState(null);
  const [selectedCustomOptions, setSelectedCustomOptions] = useState({}); // { [groupHandle]: optionLabel }
  const [customOptionInputText, setCustomOptionInputText] = useState('');
  const [customOptionImages, setCustomOptionImages] = useState([]);
  // Derived: first group's selection for backward-compat checks
  const selectedCustomOption = Object.values(selectedCustomOptions)[0] ?? null;
  const [expandedSection, setExpandedSection] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(true);
  const [selectedSet, setSelectedSet] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  
  // Custom fields for "personalni plaanj" product
  const [customDescription, setCustomDescription] = useState('');
  const [customDate, setCustomDate] = useState('');
  const [customImages, setCustomImages] = useState([]);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedBeadColors, setSelectedBeadColors] = useState([]);
  const [selectedAccessories, setSelectedAccessories] = useState([]);
  const [customAccessories, setCustomAccessories] = useState([]);
  const [secondPet, setSecondPet] = useState(false);
  const [petCount, setPetCount] = useState(1); // illustration products only
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  
  // Calculate minimum date (1 month from now)
  const getMinDate = () => {
    const today = new Date();
    const oneMonthLater = new Date(today);
    oneMonthLater.setMonth(today.getMonth() + 1);
    return oneMonthLater;
  };
  
  const minDate = getMinDate();
  
  // Format date for display
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Format date for display (readable)
  const formatDateDisplay = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('sr-Latn-RS', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  
  // Check if date is disabled (before minimum date)
  const isDateDisabled = (date) => {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const min = new Date(minDate);
    min.setHours(0, 0, 0, 0);
    return checkDate < min;
  };
  
  // Get calendar days for current month
  const getCalendarDays = () => {
    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(calendarYear, calendarMonth, day);
      days.push(date);
    }
    
    return days;
  };
  
  // Handle date selection
  const handleDateSelect = (date) => {
    if (!isDateDisabled(date)) {
      setCustomDate(formatDateForInput(date));
      setShowCalendar(false);
    }
  };
  
  // Navigate calendar months
  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (calendarMonth === 0) {
        setCalendarMonth(11);
        setCalendarYear(calendarYear - 1);
      } else {
        setCalendarMonth(calendarMonth - 1);
      }
    } else {
      if (calendarMonth === 11) {
        setCalendarMonth(0);
        setCalendarYear(calendarYear + 1);
      } else {
        setCalendarMonth(calendarMonth + 1);
      }
    }
  };
  
  // Month names (localized via the translation dictionary)
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ].map((m) => translate(m));

  // Day names (short, localized)
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => translate(d));
  
  // Check if this is the "Personalni pladanj" product
  const isPersonalniPlaanj = product && (
    product.path?.toLowerCase().includes('personalni-pladanj') ||
    product.nameBA?.toLowerCase().includes('personalni pladanj') ||
    product.nameRS?.toLowerCase().includes('personalni pladanj') ||
    product.nameUS?.toLowerCase().includes('personal pladanj') ||
    product.nameBA?.toLowerCase().includes('personalni pladanj') ||
    product.nameRS?.toLowerCase().includes('personalni pladanj')
  );

  // Check if this is the "Personalizovani privjesci sa vašim ljubimcem" product
  const isPersonalizovaniPrivjesci = product && (
    product.path?.toLowerCase().includes('personalizovani-privjesci') ||
    product.nameBA?.toLowerCase().includes('privjes') ||
    product.nameRS?.toLowerCase().includes('privez')
  );

  // Check if this is the "Personalna ljubimac soljica" product
  const isPersonalnaLjubimacSoljica = product && !isPersonalizovaniPrivjesci && (
    product.path?.toLowerCase().includes('personalna-ljubimac-soljica') ||
    product.nameBA?.toLowerCase().includes('ljubimac') ||
    product.nameRS?.toLowerCase().includes('ljubimac')
  );

  // Check if this is the "Personalni tanjir" product
  const isPersonalniTanjir = product && (
    product.path?.toLowerCase().includes('personalni-tanjir') ||
    product.nameBA?.toLowerCase().includes('personalni tanjir') ||
    product.nameRS?.toLowerCase().includes('personalni tanjir')
  );

  // Custom-illustration products (plates, jewelry dish, ornament) — only need the reference images.
  const isCustomIllustrationProduct = product && (
    product.path?.toLowerCase().includes('personalni-tanjir-sa-ilustracijom-po-zelji') ||
    product.path?.toLowerCase().includes('personalni-tanjir-sa-vasim-ljubimcem') ||
    product.path?.toLowerCase().includes('personalni-ukras-za-jelku') ||
    product.path?.toLowerCase().includes('tanjiric-za-nakit-po-zelji')
  );

  const isJewelryDish = product?.path?.toLowerCase().includes('tanjiric-za-nakit');
  const isOrnament = product?.path?.toLowerCase().includes('personalni-ukras-za-jelku');
  const isIllustrationPlate = product?.path?.toLowerCase().includes('personalni-tanjir-sa-ilustracijom-po-zelji');

  // Pet-photo products share the same accessories / two-pets / reference-image flow.
  const isPetProduct = isPersonalnaLjubimacSoljica || isPersonalizovaniPrivjesci;

  // Second-pet price profile — illustration products charge their own extra
  const secondPetProfile = isPersonalizovaniPrivjesci ? 'privjesci' : isJewelryDish ? 'nakit' : isOrnament ? 'ukras' : isCustomIllustrationProduct ? 'ilustracija' : 'default';

  // Accessories: pet-photo products plus the jewelry dish and ornament. Pendant, dish and ornament also take custom (described) ones.
  const hasAccessories = isPetProduct || isJewelryDish || isOrnament;
  const allowsCustomAccessories = isPersonalizovaniPrivjesci || isJewelryDish || isOrnament;
  const accessoryProfile = isPersonalizovaniPrivjesci ? 'privjesci' : isJewelryDish ? 'nakit' : isOrnament ? 'ukras' : 'default';

  // Products with the pet-count / reference-image flow
  const hasReferenceImages = isPetProduct || isCustomIllustrationProduct;

  // Products where at least one reference image is required (optional for the jewelry dish)
  const requiresReferenceImages = hasReferenceImages && !isJewelryDish;

  // Combined flag for products that need the custom personalization section
  const isPersonalProduct = isPersonalniPlaanj || isPersonalnaLjubimacSoljica || isPersonalizovaniPrivjesci || isCustomIllustrationProduct;

  // Reference-image limit: two pets need room for a photo of each, so allow more.
  const maxReferenceImages = isCustomIllustrationProduct
    ? Math.min(3 * petCount, MAX_ILLUSTRATION_IMAGES)
    : isPetProduct && secondPet ? 6 : 3;

  // Illustration products take any number of pets; more pets allow more photos.
  const changePetCount = (n) => {
    const count = Math.max(1, Math.min(MAX_ILLUSTRATION_PETS, n));
    setPetCount(count);
    setSecondPet(count > 1);
    setCustomImages(prev => prev.slice(0, Math.min(3 * count, MAX_ILLUSTRATION_IMAGES)));
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const oldQty = cartItems.find(item => Number(productId) === item.id)?.qty || 1;

  useEffect(() => {
    // Find product by path - this should work with the route parameter
    const selectedProduct = productsData.find(p => p.path === productPath);
    if (selectedProduct) {
      console.log('Selected Product Data:', {
        id: selectedProduct.id,
        name: selectedProduct.nameBA,
        path: selectedProduct.path,
        is_on_sale: selectedProduct.is_on_sale,
        sale_discount_percentage: selectedProduct.sale_discount_percentage,
        discounted_KM: selectedProduct.discounted_KM,
        discounted_EUR: selectedProduct.discounted_EUR,
        discounted_RSD: selectedProduct.discounted_RSD,
        KM: selectedProduct.KM,
        EUR: selectedProduct.EUR,
        RSD: selectedProduct.RSD
      });
      
      setProductId(selectedProduct.id);
      setProduct(selectedProduct);
      setCurrentImgPath(selectedProduct.imageUrl);
      pixelViewContent(selectedProduct, currency, country);
      trackEvent('product_view', { product_id: selectedProduct.id });


      const coGroups = getCustomOptionGroups(selectedProduct.custom_options);
      if (coGroups.length > 0) {
        const initSelections = {};
        coGroups.forEach(group => {
          const firstEnabled = (group.options || []).find(o => o.enabled !== false);
          if (firstEnabled) initSelections[group.customHandle] = firstEnabled.label;
        });
        setSelectedCustomOptions(initSelections);
      }

      if (selectedProduct.product_sets?.enabled && selectedProduct.product_sets.sets?.length > 0) {
        const firstAvailable = selectedProduct.product_sets.sets.find(set =>
          (set.items || []).every(setItem => {
            const p = productsData.find(pd => pd.id === setItem.product_id);
            return p && p.available && p.is_in_stock;
          })
        );
        setSelectedSet(firstAvailable || null);
      } else {
        setSelectedSet(null);
      }
    } else {
      console.log('Product not found for path:', productPath);
      console.log('Available products:', productsData.map(p => ({ id: p.id, path: p.path, name: p.nameBA })));
    }
  }, [productPath, productsData]);

  // Update main image when selected set changes
  useEffect(() => {
    if (product && selectedSet && selectedSet.image_index !== null && selectedSet.image_index !== undefined) {
      const img = product.listingImages?.[selectedSet.image_index];
      if (img) setCurrentImgPath(img);
    }
  }, [selectedSet, product]);

  // Update main image when custom option changes (only if showImages is enabled, first group only)
  useEffect(() => {
    const groups = getCustomOptionGroups(product?.custom_options);
    if (groups.length > 0) {
      const firstGroup = groups[0];
      const selectedLabel = selectedCustomOptions[firstGroup.customHandle];
      // Note: an option's own uploaded image is shown only as the option thumbnail,
      // never swapped in as the main product image.
      const selectedOpt = (firstGroup.options || []).find(o => o.label === selectedLabel);
      if (firstGroup.showImages && selectedLabel && !selectedOpt?.image && product?.listingImages) {
        const idx = (firstGroup.options || []).findIndex(o => o.label === selectedLabel);
        if (idx !== -1 && product.listingImages[idx]) {
          setCurrentImgPath(product.listingImages[idx]);
        }
      }
    }
  }, [selectedCustomOptions, product]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCalendar && !event.target.closest('.calendar-container')) {
        setShowCalendar(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);
  
  // Initialize calendar to show minimum date month
  useEffect(() => {
    if (isPersonalProduct) {
      setCalendarMonth(minDate.getMonth());
      setCalendarYear(minDate.getFullYear());
    }
  }, [isPersonalProduct]);

  // Reset custom option text input when selection changes
  useEffect(() => {
    setCustomOptionInputText('');
  }, [selectedCustomOptions]);

  // Fetch related products from the active (published) collection, fallback to featured
  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!product || !apiCountry) return;

      try {
        setRelatedLoading(true);

        const countryCode = apiCountry || "BA";
        const isPreviewMode = !!previewKey;

        // 1) Find any active (published) collection
        const activeCollection = categories?.find(cat =>
          (cat.is_collection === true || cat.is_collection === 1) &&
          (isPreviewMode || cat.is_collection_listed === true || cat.is_collection_listed === 1)
        );

        if (activeCollection) {
          const fromCollection = (productsData || [])
            .filter(p => p.category === activeCollection.id && p.id !== product.id && p.is_in_stock)
            .slice(0, 4);

          if (fromCollection.length > 0) {
            setRelatedProducts(fromCollection);
            return;
          }
        }

        // 2) Fallback: featured products
        const response = await axios.get(`${apiUrl}/api/featured-products?country=${countryCode}`);
        const featuredProducts = applyCountryPrices(response.data || [], apiCountry, getPriceGroup());
        const filtered = featuredProducts.filter(p => p.id !== product.id && p.is_in_stock).slice(0, 4);
        setRelatedProducts(filtered);
      } catch (error) {
        console.error('Error fetching related products:', error);
        const fallback = productsData.filter(p => p.id !== product.id && p.is_in_stock).slice(0, 4);
        setRelatedProducts(fallback);
      } finally {
        setRelatedLoading(false);
      }
    };

    fetchRelatedProducts();
  }, [product, productsData, apiCountry]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brownSkin"></div>
      </div>
    );
  }

  function ShowSelectedImage(imgUrl) {
    setCurrentImgPath(imgUrl);
  }

  const listingImagesSlider = () => {
    return (
      <div className="flex flex-wrap gap-2 mt-4">
        {product.listingImages.map((item, index) => (
          <button
            key={index}
            onClick={() => ShowSelectedImage(item)}
            className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
              currectImgPath === item ? 'border-brownSkin' : 'border-transparent'
            }`}
          >
            <CustomImage 
              className="h-20 w-20 object-cover hover:opacity-75 transition-opacity duration-300" 
              alt="Product thumbnail" 
              src={item} 
            />
          </button>
        ))}
      </div>
    );
  };

  // Default product information
  const getDefaultProductInfo = () => (
    <>
      <p>
        {translate("Each product is handmade and carries its own unique character. Due to handmade production, minor variations in shape, size, color, and pattern are possible, which makes each piece special and one of a kind.")}
      </p>
      <p>
        {translate("Made of porcelain, painted with glaze colors and covered with a transparent glaze with 24K gold details.")}
      </p>
      <p>
        {translate("All materials used are lead-free, non-toxic, and completely safe for use with food.")}
      </p>
      <p>{translate("To preserve the gold details and lasting shine, gentle hand washing is recommended.")}</p>
    </>
  );

  // Default delivery information
  const getDefaultDeliveryInfo = () => (
    <>
      <p>
        {translate("The estimated delivery date is shown at checkout.")}<br />
        {translate("The delivery cost will be included at checkout.")}<br />
        {translate("Delivery is free for orders over 150 KM / 75 EUR / 9000 RSD.")}
      </p>
    </>
  );

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title={`Bulka Ceramics - ${localized(product, 'name', country)}`}
        description={localized(product, 'description', country)}
        type="website"
        name="Bulka Ceramics"
      />
      
      {showModal && <NotifyAvailable onClose={closeModal} notifyProductId={notifyProductId} />}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-6">
            <div className="relative  overflow-hidden bg-white ">
              <CustomImage
                src={currectImgPath}
                alt={product.imageAlt}
                className="w-full max-h-[600px] object-contain object-left transition-opacity duration-500 ease-in-out "
              />
          </div>
          {product.listingImages && product.listingImages.length > 1 && listingImagesSlider()}
          </div>

          {/* Product Info */}
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                {localized(product, 'name', country)}
              </h1>
              {/* Limited Stock Notice - Only for New Year Products */}
    
              <p className="text-brownSkin/80 text-sm font-medium mb-3 italic ">
                *{translate("Limited stock")}
              </p>
            
              <PriceDisplayWithCustomOption product={product} currency={currency} customOption={serializeCustomOptionSelections(product?.custom_options, selectedCustomOptions)} selectedSet={selectedSet} className="mb-6" />
              
              {/* Description for personal products - shown right under price */}
              {isPersonalProduct && (
                <div className="prose prose-sm text-gray-600 mt-4">
                  {localized(product, 'description', country).split('\n').map((line, index) => (
                    <p key={index} className="mb-2">{line}</p>
                  ))}
                </div>
              )}
            </div>

            {/* Delivery Date Range */}
            {(() => {
              const selectedSerialized = serializeCustomOptionSelections(product.custom_options, selectedCustomOptions);
              const deliveryDates = formatLeadDates(getItemLeadDays(product, apiCountry, selectedSerialized));

              return (
                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-xs text-gray-600">
                      {translate("Delivery within")}{' '}
                      <span className="text-gray-700">{deliveryDates.start}</span>
                      {' - '}
                      <span className="text-gray-700">{deliveryDates.end}</span>
                    </p>
                  </div>
                  {isPersonalnaLjubimacSoljica && (
                    <p className="text-xs text-gray-400 italic mt-1 ml-6">
                      {translate("Due to handmade production and personalization, the mug takes about 4 weeks to make.")}
                    </p>
                  )}
                  {isPersonalniTanjir && (
                    <p className="text-xs text-gray-400 italic mt-1 ml-6">
                      {translate("Due to handmade production and personalization, the plate takes about 4 weeks to make.")}
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Stock Status Display - Only show if completely out of stock */}
            {product.stock_tracking_enabled && product.stock_quantity === 0 && (
              <div className="bg-red-50 p-4 rounded-lg mb-6">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                  </svg>
                  <span className="text-sm font-medium text-red-800">
                    {translate("Product is currently out of stock")}
                  </span>
                </div>
              </div>
            )}

            {product.available && product.is_in_stock ? (
              <div className="space-y-6">
                {/* Custom fields for personal products */}
                {isPersonalProduct && (
                  <div className="mt-8 space-y-4 border-t border-gray-200 pt-6">
                    {/* Section Header */}
                    <div className="border-b border-gray-200 pb-4">
                      <h3 className="text-lg font-medium text-gray-900">{translate("Additional information")}</h3>
                    </div>

                    <div className="space-y-6">
                      {/* Text input — different per product (pendants and illustration products have no text option) */}
                      {!isPersonalizovaniPrivjesci && !isCustomIllustrationProduct && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label htmlFor="customDescription" className="block text-sm font-medium text-gray-700">
                            {isPersonalnaLjubimacSoljica ? translate('Gold text inside the mug (optional)') : translate('Describe the details you want on the platter:')}
                          </label>
                          <span className={`text-xs ${customDescription.length > (isPersonalnaLjubimacSoljica ? 20 : 300) ? 'text-red-500' : 'text-gray-500'}`}>
                            {customDescription.length}/{isPersonalnaLjubimacSoljica ? 20 : 300}
                          </span>
                        </div>
                        {isPersonalnaLjubimacSoljica ? (
                          <input
                            type="text"
                            id="customDescription"
                            value={customDescription}
                            onChange={(e) => {
                              if (e.target.value.length <= 20) setCustomDescription(e.target.value);
                            }}
                            maxLength={20}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brownSkin focus:border-brownSkin text-gray-900 transition-colors duration-200"
                            placeholder={translate("e.g. I love you ❤️")}
                          />
                        ) : (
                          <textarea
                            id="customDescription"
                            value={customDescription}
                            onChange={(e) => {
                              if (e.target.value.length <= 300) setCustomDescription(e.target.value);
                            }}
                            rows={4}
                            maxLength={300}
                            required
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brownSkin focus:border-brownSkin text-gray-900 transition-colors duration-200"
                            placeholder={translate("Example: We have a dog, she loves walks, could be a cake and balloons, or a cup of tea")}
                          />
                        )}
                      </div>
                      )}

                      {/* Date Picker — only for pladanj */}
                      {isPersonalniPlaanj && (
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {translate("Date you need the product")} *
                          </label>
                          <div className="relative calendar-container">
                            <input
                              type="text"
                              readOnly
                              value={customDate ? formatDateDisplay(customDate) : ''}
                              onClick={() => setShowCalendar(!showCalendar)}
                              placeholder={translate("Choose a date")}
                              required
                              className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brownSkin focus:border-brownSkin text-gray-900 transition-colors duration-200 cursor-pointer"
                            />
                            <button
                              type="button"
                              onClick={() => setShowCalendar(!showCalendar)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-brownSkin transition-colors"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </button>

                            {showCalendar && (
                              <div className="absolute z-50 mt-2 bg-white border-2 border-gray-300 rounded-lg shadow-xl p-4 w-full max-w-sm calendar-container">
                                <div className="flex items-center justify-between mb-4">
                                  <button type="button" onClick={() => navigateMonth('prev')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                    </svg>
                                  </button>
                                  <h3 className="text-lg font-semibold text-gray-900">{monthNames[calendarMonth]} {calendarYear}</h3>
                                  <button type="button" onClick={() => navigateMonth('next')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                  </button>
                                </div>
                                <div className="grid grid-cols-7 gap-1 mb-2">
                                  {dayNames.map((day, index) => (
                                    <div key={index} className="text-center text-xs font-medium text-gray-500 py-2">{day}</div>
                                  ))}
                                </div>
                                <div className="grid grid-cols-7 gap-1">
                                  {getCalendarDays().map((date, index) => {
                                    if (!date) return <div key={index} className="aspect-square"></div>;
                                    const isDisabled = isDateDisabled(date);
                                    const isSelected = customDate && formatDateForInput(date) === customDate;
                                    const isToday = formatDateForInput(date) === formatDateForInput(new Date());
                                    return (
                                      <button
                                        key={index}
                                        type="button"
                                        onClick={() => handleDateSelect(date)}
                                        disabled={isDisabled}
                                        className={`aspect-square rounded-lg text-sm font-medium transition-all duration-200 ${
                                          isDisabled ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                                          : isSelected ? 'bg-brownSkin text-white shadow-md'
                                          : isToday ? 'bg-brownSkin/20 text-brownSkin font-semibold hover:bg-brownSkin/30'
                                          : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                      >
                                        {date.getDate()}
                                      </button>
                                    );
                                  })}
                                </div>
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <p className="text-xs text-gray-500 text-center">
                                    {translate("Earliest available date")}: <span className="font-semibold text-brownSkin">{formatDateDisplay(minDate)}</span>
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {translate("The product requires at least 1 month to make. Please choose a date at least 1 month from today")} ({formatDateDisplay(minDate)} {translate("or later")}).
                          </p>
                          <input type="hidden" value={customDate} required />
                        </div>
                      )}

                      {/* Color Selection */}
                      {!isCustomIllustrationProduct && (
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">
                          {isPersonalizovaniPrivjesci ? translate("Choose a bead color") : translate("Choose a color")} *
                        </label>
                        {isPersonalizovaniPrivjesci ? (
                          <>
                            <div className="flex flex-wrap gap-2.5">
                              {PENDANT_COLORS.map(({ key, hex, darkCheck }) => {
                                const isSelected = selectedBeadColors.includes(key);
                                return (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => setSelectedBeadColors(prev =>
                                    isSelected ? prev.filter(c => c !== key) : [...prev, key]
                                  )}
                                  title={key}
                                  className={`w-11 h-11 rounded-lg border-2 transition-all duration-200 ${
                                    isSelected
                                      ? 'border-brownSkin shadow-md ring-2 ring-brownSkin/20'
                                      : 'border-gray-300 hover:border-gray-400'
                                  }`}
                                  style={{ backgroundColor: hex }}
                                  aria-label={key}
                                >
                                  {isSelected && (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <svg className={`w-5 h-5 ${darkCheck ? 'text-gray-700' : 'text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                  )}
                                </button>
                                );
                              })}
                            </div>
                            <p className="text-xs text-gray-600">
                              {translate("You can choose more than one color.")} {selectedBeadColors.length > 0 && (
                                <span className="font-semibold text-brownSkin">{selectedBeadColors.join(', ')}</span>
                              )}
                            </p>
                          </>
                        ) : isPersonalnaLjubimacSoljica ? (
                          <>
                            <div className="flex items-center gap-4">
                              {[
                                { key: 'zelena', hex: '#b4e0c2', label: translate('Green'), darkCheck: false },
                                { key: 'roza',   hex: '#F9A8D4', label: translate('Pink'),   darkCheck: false },
                                { key: 'plava',  hex: '#60A5FA', label: translate('Blue'),  darkCheck: false },
                                { key: 'bijela', hex: '#f7e4c3', label: translate('White/beige'), darkCheck: true },
                              ].map(({ key, hex, label, darkCheck }) => (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => setSelectedColor(key)}
                                  className={`w-12 h-12 rounded-lg border-2 transition-all duration-200 ${
                                    selectedColor === key
                                      ? 'border-brownSkin shadow-md ring-2 ring-brownSkin/20'
                                      : 'border-gray-300 hover:border-gray-400'
                                  }`}
                                  style={{ backgroundColor: hex }}
                                  aria-label={label}
                                >
                                  {selectedColor === key && (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <svg className={`w-5 h-5 ${darkCheck ? 'text-gray-600' : 'text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                            <div className="flex gap-6 text-xs text-gray-600">
                              {[{ key: 'zelena', label: translate('Green') }, { key: 'roza', label: translate('Pink') }, { key: 'plava', label: translate('Blue') }, { key: 'bijela', label: translate('White/beige') }].map(({ key, label }) => (
                                <span key={key} className={selectedColor === key ? 'font-semibold text-brownSkin' : ''}>{label}</span>
                              ))}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-4">
                              {[
                                { key: 'plava', hex: '#60A5FA', label: translate('Blue') },
                                { key: 'crna',  hex: '#374151', label: translate('Black') },
                                { key: 'roza',  hex: '#F9A8D4', label: translate('Pink') },
                              ].map(({ key, hex, label }) => (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => setSelectedColor(key)}
                                  className={`w-12 h-12 rounded-lg border-2 transition-all duration-200 ${
                                    selectedColor === key
                                      ? 'border-brownSkin shadow-md ring-2 ring-brownSkin/20'
                                      : 'border-gray-300 hover:border-gray-400'
                                  }`}
                                  style={{ backgroundColor: hex }}
                                  aria-label={label}
                                >
                                  {selectedColor === key && (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                            <div className="flex gap-6 text-xs text-gray-600">
                              {[{ key: 'plava', label: translate('Blue') }, { key: 'crna', label: translate('Black') }, { key: 'roza', label: translate('Pink') }].map(({ key, label }) => (
                                <span key={key} className={selectedColor === key ? 'font-semibold text-brownSkin' : ''}>{label}</span>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      )}

                      {/* Pets + accessories. Pendants show accessories first, then pets. */}
                      <div className={`flex flex-col gap-6 ${isPersonalizovaniPrivjesci ? 'flex-col-reverse' : ''}`}>
                      {/* Number of pets — pet-photo and illustration products (not the illustration plate or ornament) */}
                      {hasReferenceImages && !isIllustrationPlate && !isOrnament && (
                        <div className="space-y-3 border-t border-gray-200 pt-4">
                          <h3 className="text-sm font-medium text-gray-900">{isPersonalizovaniPrivjesci ? translate("How many pets on the pendant?")
                            : isCustomIllustrationProduct ? (isJewelryDish ? translate("How many pets on the jewelry dish?") : translate("How many pets on the plate?"))
                            : translate("How many pets on the mug?")}</h3>
                          {isCustomIllustrationProduct ? (
                            <div className="flex items-center gap-4">
                              <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() => changePetCount(petCount - 1)}
                                  disabled={petCount <= 1}
                                  aria-label="-"
                                  className="w-10 h-10 flex items-center justify-center text-lg text-gray-700 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent transition-colors"
                                >−</button>
                                <span className="w-12 text-center text-base font-semibold text-gray-900">{petCount}</span>
                                <button
                                  type="button"
                                  onClick={() => changePetCount(petCount + 1)}
                                  disabled={petCount >= MAX_ILLUSTRATION_PETS}
                                  aria-label="+"
                                  className="w-10 h-10 flex items-center justify-center text-lg text-gray-700 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent transition-colors"
                                >+</button>
                              </div>
                              <span className="text-xs text-gray-600">
                                {translate("Each additional pet")} <span className="text-brownSkin font-medium">+{getSecondPetPrice(currency, secondPetProfile).toFixed(2)} {currency}</span>
                              </span>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-3">
                              {[
                                { pets: 1, value: false, title: translate("1 pet"), sub: '' },
                                { pets: 2, value: true, title: translate("2 pets"), sub: `+${getSecondPetPrice(currency, secondPetProfile).toFixed(2)} ${currency}` },
                              ].map(({ pets, value, title, sub }) => {
                                const isSelected = secondPet === value;
                                return (
                                  <button
                                    key={pets}
                                    type="button"
                                    onClick={() => {
                                      setSecondPet(value);
                                      // Switching back to one pet lowers the image limit — trim extras.
                                      if (!value) setCustomImages(prev => prev.slice(0, 3));
                                    }}
                                    className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all duration-200 ${
                                      isSelected
                                        ? 'border-brownSkin bg-brownSkin/5 shadow-sm'
                                        : 'border-gray-200 bg-white hover:border-brownSkin/50'
                                    }`}
                                  >
                                    <span className="text-2xl leading-none" aria-hidden="true">
                                      {pets === 1 ? '🐾' : '🐾🐾'}
                                    </span>
                                    <span className="flex flex-col">
                                      <span className={`text-sm font-semibold ${isSelected ? 'text-brownSkin' : 'text-gray-900'}`}>{title}</span>
                                      {sub && (
                                        <span className={`text-xs ${value ? 'text-brownSkin font-medium' : 'text-gray-500'}`}>{sub}</span>
                                      )}
                                    </span>
                                    <span className={`ml-auto w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                      isSelected ? 'border-brownSkin bg-brownSkin' : 'border-gray-300'
                                    }`}>
                                      {isSelected && <span className="w-2 h-2 rounded-full bg-white"></span>}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Accessories — pet-photo products and jewelry dish */}
                      {hasAccessories && (
                        <div className="space-y-3 border-t border-gray-200 pt-4">
                          <h3 className="text-sm font-medium text-gray-900">{translate("Add accessories (optional)")}</h3>
                          {isPersonalizovaniPrivjesci && (
                            <p className="text-xs text-green-600 font-medium">{translate("Your first accessory is free.")}</p>
                          )}

                          <div className="grid grid-cols-7 gap-1.5">
                            {getAccessoriesForProfile(accessoryProfile).map(acc => {
                              const isSelected = selectedAccessories.includes(acc.id);
                              return (
                                <div key={acc.id} className="relative group">
                                  {/* Hover tooltip */}
                                  <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 hidden group-hover:flex flex-col items-center bg-white border border-gray-200 rounded-xl shadow-xl w-[200px] p-4">
                                    {acc.image ? (
                                      <img src={acc.image} alt={getAccessoryLabel(acc, country)} className="w-[140px] h-[140px] object-contain" />
                                    ) : (
                                      <span className="text-6xl leading-none">{acc.emoji}</span>
                                    )}
                                    <span className="text-xs text-gray-700 text-center mt-3 leading-snug">{getAccessoryDescription(acc, accessoryProfile, country)}</span>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => setSelectedAccessories(prev =>
                                      isSelected ? prev.filter(id => id !== acc.id) : [...prev, acc.id]
                                    )}
                                    className={`relative w-16 h-16 flex flex-col items-center justify-center p-1 rounded-lg border-2 transition-all duration-200 aspect-square w-full ${
                                      isSelected
                                        ? 'border-brownSkin bg-brownSkin/10 shadow-sm'
                                        : 'border-gray-200 bg-white hover:border-gray-400'
                                    }`}
                                  >
                                    {acc.starred && (
                                      <span className="absolute top-0.5 right-0.5 text-[8px] leading-none">⭐</span>
                                    )}
                                    {acc.image ? (
                                      <img src={acc.image} alt={getAccessoryLabel(acc, country)} className="w-full h-full object-contain" />
                                    ) : (
                                      <>
                                        <span className="text-2xl leading-none">{acc.emoji}</span>
                                        <span className="text-[9px] text-gray-600 leading-tight text-center mt-0.5 line-clamp-1">{getAccessoryLabel(acc, country)}</span>
                                      </>
                                    )}
                                  </button>
                                </div>
                              );
                            })}
                            {/* Add-custom tile — same size as an accessory, pendant and jewelry dish */}
                            {allowsCustomAccessories && (
                              <button
                                type="button"
                                onClick={() => setCustomAccessories(prev => [...prev, ''])}
                                aria-label={translate("Add your own accessory")}
                                title={translate("Add your own accessory")}
                                className="w-16 h-16 flex items-center justify-center p-1 rounded-lg border-2 border-dashed border-gray-300 bg-white text-gray-400 hover:border-brownSkin hover:text-brownSkin transition-all duration-200 aspect-square w-full"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                </svg>
                              </button>
                            )}
                          </div>

                          {selectedAccessories.length > 0 && (
                            <p className="text-sm text-brownSkin font-medium">
                              +{getAccessoriesTotalPrice(selectedAccessories, currency, { firstFree: isPersonalizovaniPrivjesci, profile: accessoryProfile }).toFixed(2)} {currency} {translate("for")} {selectedAccessories.length} {selectedAccessories.length === 1 ? translate("accessory") : translate("accessories (count)")}
                            </p>
                          )}

                          {/* Custom (user-described) accessories — pendant and jewelry dish */}
                          {allowsCustomAccessories && (
                            <div className="pt-2 space-y-2">
                              <p className="text-xs text-gray-600">
                                {translate("Want something else? Describe your own accessory")} <span className="text-brownSkin font-medium">(+{getCustomAccessoryPrice(currency, accessoryProfile).toFixed(2)} {currency} {translate("each")})</span>
                              </p>

                              {customAccessories.map((text, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={text}
                                    maxLength={80}
                                    onChange={(e) => setCustomAccessories(prev => prev.map((t, i) => i === idx ? e.target.value : t))}
                                    placeholder={translate("e.g. a small crown, a bow tie, a fish…")}
                                    className="flex-1 border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brownSkin focus:border-brownSkin text-gray-900 transition-colors duration-200"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setCustomAccessories(prev => prev.filter((_, i) => i !== idx))}
                                    aria-label={translate("Remove")}
                                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-gray-500 hover:text-red-600 hover:border-red-300 transition-colors"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}

                              {getValidCustomAccessories(customAccessories).length > 0 && (
                                <p className="text-sm text-brownSkin font-medium">
                                  +{(getValidCustomAccessories(customAccessories).length * getCustomAccessoryPrice(currency, accessoryProfile)).toFixed(2)} {currency} {translate("for")} {getValidCustomAccessories(customAccessories).length} {translate("custom accessories")}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      </div>

                      {/* Image Uploads — optional for pladanj, required for pet and illustration products */}
                      <div className="space-y-2 border-t border-gray-200 pt-4">
                        <div className="flex items-center justify-between">
                          <label htmlFor="customImages" className="block text-sm font-medium text-gray-700">
                            {petCount > 2 ? translate("Reference images of all pets") : secondPet ? translate("Reference images of both pets") : translate("Reference images")} {requiresReferenceImages ? '*' : `(${translate("optional")})`}
                          </label>
                          <span className="text-xs text-gray-500">{customImages.length}/{maxReferenceImages}</span>
                        </div>
                        <input
                          type="file"
                          id="customImages"
                          multiple
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          disabled={customImages.length >= maxReferenceImages}
                          onChange={async (e) => {
                            const newFiles = Array.from(e.target.files);
                            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
                            const invalidFiles = newFiles.filter(f => !allowedTypes.includes(f.type));
                            if (invalidFiles.length > 0) {
                              alert(`${translate("The following files are not allowed (only images are allowed):")} ${invalidFiles.map(f => f.name).join(', ')}`);
                            }
                            const validFiles = newFiles.filter(f => allowedTypes.includes(f.type));
                            if (validFiles.length > 0) {
                              try {
                                const compressImage = (file) => new Promise((resolve, reject) => {
                                  const reader = new FileReader();
                                  reader.onload = (e) => {
                                    const img = new Image();
                                    img.onload = () => {
                                      const canvas = document.createElement('canvas');
                                      const ctx = canvas.getContext('2d');
                                      const MAX = 800;
                                      let w = img.width, h = img.height;
                                      if (w > h) { if (w > MAX) { h = Math.round(h * MAX / w); w = MAX; } }
                                      else { if (h > MAX) { w = Math.round(w * MAX / h); h = MAX; } }
                                      canvas.width = w; canvas.height = h;
                                      ctx.drawImage(img, 0, 0, w, h);
                                      canvas.toBlob((blob) => {
                                        if (blob) resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
                                        else reject(new Error('Failed to compress image'));
                                      }, 'image/jpeg', 0.6);
                                    };
                                    img.onerror = reject;
                                    img.src = e.target.result;
                                  };
                                  reader.onerror = reject;
                                  reader.readAsDataURL(file);
                                });
                                const compressed = await Promise.all(validFiles.map(compressImage));
                                const remaining = maxReferenceImages - customImages.length;
                                setCustomImages([...customImages, ...compressed.slice(0, remaining)]);
                              } catch (error) {
                                console.error('Error compressing images:', error);
                                alert(translate('Error processing images. Please try again.'));
                              }
                            }
                            const input = document.getElementById('customImages');
                            if (input) input.value = '';
                          }}
                          className={`w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brownSkin focus:border-brownSkin text-gray-900 transition-colors duration-200 ${
                            customImages.length >= maxReferenceImages ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer'
                          }`}
                        />
                        {customImages.length > 0 && (
                          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {customImages.map((file, index) => (
                              <div key={index} className="relative group">
                                <img src={URL.createObjectURL(file)} alt={`Preview ${index + 1}`} className="w-full h-24 object-cover rounded-lg border-2 border-gray-200" />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCustomImages(customImages.filter((_, i) => i !== index));
                                    const input = document.getElementById('customImages');
                                    if (input) input.value = '';
                                  }}
                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                >×</button>
                              </div>
                            ))}
                          </div>
                        )}
                        {customImages.length < maxReferenceImages ? (
                          <p className="text-xs text-gray-500">
                            {requiresReferenceImages ? `${secondPet ? translate("Add at least one clear photo of each pet.") : translate("You must add at least 1 image.")} ` : ''}{translate("You can add up to")} {maxReferenceImages} {translate("images")}. {translate("Allowed formats: JPEG, PNG, GIF, WEBP.")}
                          </p>
                        ) : (
                          <p className="text-xs text-green-600 font-medium">✓ {translate("You've added the maximum number of images.")} ({maxReferenceImages}/{maxReferenceImages})</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {(() => {
                  const coGroups = getCustomOptionGroups(product.custom_options);
                  if (coGroups.length === 0) return null;
                  // Find any selected option with a custom text input enabled
                  let selectedOptForInput = null;
                  let selectedOptForImageUpload = null;
                  coGroups.forEach(group => {
                    const selLabel = selectedCustomOptions[group.customHandle];
                    const opt = (group.options || []).find(o => o.label === selLabel);
                    if (opt?.customInput?.enabled) selectedOptForInput = opt;
                    if (opt?.customImageUpload?.enabled) selectedOptForImageUpload = opt;
                  });
                  return (
                    <div className="space-y-5">
                      {coGroups.map((group, gi) => {
                        const groupSelectedLabel = selectedCustomOptions[group.customHandle];

                        // Standalone text field (no options) — acts like a form input.
                        if (group.type === 'text') {
                          const maxChars = group.maxChars || 100;
                          const textVal = groupSelectedLabel || '';
                          return (
                            <div key={gi} className="space-y-1">
                              <label className="block text-sm font-medium text-gray-700">
                                {pickGroupName(group, country)}{group.required ? ' *' : ''}
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={textVal}
                                  maxLength={maxChars}
                                  onChange={(e) => setSelectedCustomOptions(prev => ({ ...prev, [group.customHandle]: e.target.value }))}
                                  placeholder={pickLabel(group, country, 'placeholder')}
                                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brownSkin focus:border-brownSkin text-gray-900 transition-colors duration-200"
                                />
                                <span className="absolute right-2 bottom-2 text-xs text-gray-400 pointer-events-none">
                                  {textVal.length}/{maxChars}
                                </span>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div key={gi} className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              {pickGroupName(group, country)}
                            </label>
                            <div className="grid grid-cols-1 gap-3">
                              {(group.options || []).map((option, index) => {
                                const optionImage = option.image
                                  ? option.image
                                  : (group.showImages && product.listingImages && product.listingImages[index])
                                    ? product.listingImages[index] : null;
                                const isOutOfStock = product.stock_tracking_enabled && product.custom_option_stocks ? (() => {
                                  const stockInfo = product.custom_option_stocks.find(s => s.custom_option === option.label);
                                  return stockInfo ? stockInfo.quantity === 0 : false;
                                })() : false;
                                const isOptionDisabled = option.enabled === false || isOutOfStock;
                                const isSelected = groupSelectedLabel === option.label;
                                return (
                                  <div key={index} className="relative">
                                    <label className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                                      isSelected ? 'border-brownSkin bg-brownSkin/5' : 'border-gray-200 hover:border-brownSkin/50'
                                    } ${isOptionDisabled ? 'opacity-50 cursor-not-allowed' : ''} ${isOutOfStock ? 'border-red-200 bg-red-50' : ''}`}>
                                      <input
                                        type="radio"
                                        name={`customOptions_group_${gi}`}
                                        value={option.label}
                                        checked={isSelected}
                                        onChange={() => setSelectedCustomOptions(prev => ({ ...prev, [group.customHandle]: option.label }))}
                                        disabled={isOptionDisabled}
                                        className="sr-only"
                                      />
                                      {optionImage && (
                                        <div className="flex-shrink-0 mr-3">
                                          <CustomImage src={optionImage} alt={option.label}
                                            className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                                        </div>
                                      )}
                                      <div className="flex-1">
                                        <span className={`text-sm font-medium ${isSelected ? 'text-brownSkin' : 'text-gray-900'} ${isOutOfStock ? 'text-red-600' : ''}`}>
                                          {pickLabel(option, country)}
                                          {option.priceModifier && option.priceModifier[currency] > 0 && (
                                            <span className="ml-2 text-xs text-gray-600 font-normal">
                                              (+{option.priceModifier[currency]} {currency})
                                            </span>
                                          )}
                                        </span>
                                        {option.description && (
                                          <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                                        )}
                                        {(() => {
                                          const badge = getOptionBadge(option, apiCountry);
                                          const extra = getOptionExtraDays(product, apiCountry, option);
                                          const slower = extra && extra.deltaMax > 0;
                                          if (!badge && !extra) return null;

                                          // How long this option actually takes: its own override when it has
                                          // one, otherwise the product/country baseline it falls back to.
                                          const baseline = getProductLeadDays(product, apiCountry);
                                          const lead = extra
                                            ? { min: extra.optMin, max: extra.optMax }
                                            : { min: baseline.startDays, max: baseline.endDays };

                                          const clock = (
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                          );

                                          return (
                                            <div className="flex flex-wrap items-center gap-1 mt-1">
                                              {badge && (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-full px-2 py-0.5">
                                                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                                                  {badge}
                                                </span>
                                              )}
                                              {/* Say when it arrives, not just a bare number; when the option is
                                                  slower than the baseline, also say by how much. */}
                                              <span className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2 py-0.5 border ${
                                                slower
                                                  ? 'text-amber-700 bg-amber-50 border-amber-200'
                                                  : 'text-gray-700 bg-gray-100 border-gray-300'
                                              }`}>
                                                {clock}
                                                {translate("Delivery time")} {lead.min}–{lead.max} {translate("days")}
                                                {slower ? ` (+${extra.deltaMax})` : ''}
                                              </span>
                                            </div>
                                          );
                                        })()}
                                        {isOutOfStock && (
                                          <div className="text-xs text-red-500 mt-1 font-medium">{translate("Out of stock")}</div>
                                        )}
                                      </div>
                                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                        isSelected ? 'border-brownSkin bg-brownSkin' : isOutOfStock ? 'border-red-300 bg-red-100' : 'border-gray-300'
                                      }`}>
                                        {isSelected && !isOutOfStock && <div className="w-2 h-2 rounded-full bg-white"></div>}
                                        {isOutOfStock && <div className="w-2 h-2 rounded-full bg-red-500"></div>}
                                      </div>
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}

                      {/* Custom text input — shown when a selected option has customInput enabled */}
                      {selectedOptForInput?.customInput?.enabled && (
                        <div className="mt-2 space-y-1">
                          <label className="block text-sm font-medium text-gray-700">
                            {pickLabel(selectedOptForInput.customInput, country)}
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={customOptionInputText}
                              onChange={e => setCustomOptionInputText(e.target.value)}
                              placeholder={selectedOptForInput.customInput.placeholder}
                              maxLength={selectedOptForInput.customInput.maxChars}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brownSkin focus:border-transparent"
                            />
                            <span className="absolute right-2 bottom-2 text-xs text-gray-400 pointer-events-none">
                              {customOptionInputText.length}/{selectedOptForInput.customInput.maxChars}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Image upload — shown when a selected option has customImageUpload enabled */}
                      {selectedOptForImageUpload?.customImageUpload?.enabled && (() => {
                        const maxImgs = selectedOptForImageUpload.customImageUpload.maxImages ?? 3;
                        return (
                          <div className="mt-2 space-y-2 border-t border-gray-200 pt-3">
                            <div className="flex items-center justify-between">
                              <label className="block text-sm font-medium text-gray-700">
                                {pickLabel(selectedOptForImageUpload.customImageUpload, country)}
                              </label>
                              <span className="text-xs text-gray-500">{customOptionImages.length}/{maxImgs}</span>
                            </div>
                            <input
                              type="file"
                              id="customOptionImages"
                              multiple
                              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                              disabled={customOptionImages.length >= maxImgs}
                              onChange={async (e) => {
                                const newFiles = Array.from(e.target.files);
                                const allowed = ['image/jpeg','image/jpg','image/png','image/gif','image/webp'];
                                const valid = newFiles.filter(f => allowed.includes(f.type));
                                if (valid.length > 0) {
                                  try {
                                    const compress = (file) => new Promise((res, rej) => {
                                      const reader = new FileReader();
                                      reader.onload = (ev) => {
                                        const img = new Image();
                                        img.onload = () => {
                                          const canvas = document.createElement('canvas');
                                          const MAX = 800;
                                          let w = img.width, h = img.height;
                                          if (w > h) { if (w > MAX) { h = Math.round(h * MAX / w); w = MAX; } }
                                          else { if (h > MAX) { w = Math.round(w * MAX / h); h = MAX; } }
                                          canvas.width = w; canvas.height = h;
                                          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                                          canvas.toBlob(blob => blob ? res(new File([blob], file.name, { type: 'image/jpeg' })) : rej(), 'image/jpeg', 0.6);
                                        };
                                        img.onerror = rej;
                                        img.src = ev.target.result;
                                      };
                                      reader.onerror = rej;
                                      reader.readAsDataURL(file);
                                    });
                                    const compressed = await Promise.all(valid.map(compress));
                                    const remaining = maxImgs - customOptionImages.length;
                                    setCustomOptionImages(prev => [...prev, ...compressed.slice(0, remaining)]);
                                  } catch { alert(translate('Error processing images.')); }
                                }
                                const input = document.getElementById('customOptionImages');
                                if (input) input.value = '';
                              }}
                              className={`w-full border-2 border-gray-300 rounded-lg px-4 py-3 text-sm transition-colors ${customOptionImages.length >= maxImgs ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer'}`}
                            />
                            {customOptionImages.length > 0 && (
                              <div className="grid grid-cols-3 gap-2 mt-2">
                                {customOptionImages.map((file, idx) => (
                                  <div key={idx} className="relative group">
                                    <img src={URL.createObjectURL(file)} alt={`Preview ${idx + 1}`} className="w-full h-20 object-cover rounded-lg border-2 border-gray-200" />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setCustomOptionImages(prev => prev.filter((_, i) => i !== idx));
                                        const input = document.getElementById('customOptionImages');
                                        if (input) input.value = '';
                                      }}
                                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >×</button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <p className="text-xs text-gray-500">{translate("You can add up to")} {maxImgs} {maxImgs === 1 ? translate("image") : translate("images")}. {translate("Allowed formats: JPEG, PNG, GIF, WEBP.")}</p>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}

              {/* Set selector — shown only when product has sets enabled */}
              {product.product_sets?.enabled && product.product_sets.sets?.length > 0 && (
                <div className="space-y-2">
                  <span className="text-sm font-semibold text-gray-700">{translate("Choose a set")}</span>
                  <div className="flex items-center gap-3 flex-wrap">
                    {product.product_sets.sets.map((set, idx) => {
                      const isActive = selectedSet?.label === set.label;
                      const isSetAvailable = (set.items || []).every(setItem => {
                        const p = productsData.find(pd => pd.id === setItem.product_id);
                        return p && p.available && p.is_in_stock;
                      });
                      const isLegacyImage = set.button_image === 'setof4' || set.button_image === 'setof1';
                      const imgSrc = isLegacyImage
                        ? null
                        : (set.button_image ? `${imgUrl}/${set.button_image.replace('public/', 'storage/')}` : null);
                      const imgClass = 'h-14 w-auto max-w-[9rem] object-contain';
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => isSetAvailable && setSelectedSet(set)}
                          disabled={!isSetAvailable}
                          className={`flex flex-col items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                            !isSetAvailable
                              ? 'border-gray-200 opacity-40 cursor-not-allowed'
                              : isActive
                                ? 'border-brownSkin bg-brownSkin/5'
                                : 'border-gray-200 hover:border-brownSkin/40'
                          }`}
                        >
                          {imgSrc && <img src={imgSrc} alt={set.label} className={`${imgClass} ${!isSetAvailable ? 'grayscale' : ''}`} />}
                          <span className={`text-xs font-medium ${!isSetAvailable ? 'text-gray-400' : isActive ? 'text-brownSkin' : 'text-gray-500'}`}>
                            {pickLabel(set, country)}{!isSetAvailable ? ` – ${translate("Sold out")}` : ''}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Količina */}
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-gray-700">{translate("Quantity")}</span>
                <div className="flex items-center h-11 w-36 rounded-full border border-gray-200 overflow-hidden">
                  <Quantity
                    oldQty={oldQty}
                    id={productId}
                    onHandle={ChangeQty}
                    setCurrentQuantity={setCurrentQuantity}
                  />
                </div>
              </div>

              {/* Low stock indicator under quantity */}
              {product.stock_tracking_enabled && typeof product.stock_quantity === 'number' && product.stock_quantity > 0 && product.stock_quantity < 5 && (
                <div className="flex items-center justify-start mt-1">
                  <span className="relative flex h-3 w-3 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-500"></span>
                  </span>
                  <span className="text-sm text-black font-medium">
                    {translate("Low stock")} - {product.stock_quantity} {translate("items left")}
                  </span>
                </div>
              )}

              {product.stock_tracking_enabled && typeof product.stock_quantity === 'number' && product.stock_quantity >= 5 && (
                <div className="flex items-center justify-start mt-1">
                  <span className="relative flex h-3 w-3 mr-2">
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                  </span>
                  <span className="text-sm text-black font-medium">
                    {translate("In stock")}
                  </span>
                </div>
              )}

                <div className="flex flex-col space-y-4">
                  {/* Check if selected custom option is out of stock */}
                  {(() => {
                    const isSelectedOptionOutOfStock = selectedCustomOption && product.stock_tracking_enabled && product.custom_option_stocks ? (() => {
                      const stockInfo = product.custom_option_stocks.find(stock => 
                        stock.custom_option === selectedCustomOption
                      );
                      return stockInfo ? stockInfo.quantity === 0 : false;
                    })() : false;
                    
                    const isSelectedSetUnavailable = selectedSet
                      ? !(selectedSet.items || []).every(setItem => {
                          const p = productsData.find(pd => pd.id === setItem.product_id);
                          return p && p.available && p.is_in_stock;
                        })
                      : false;

                    const isButtonDisabled = isSelectedOptionOutOfStock || isSelectedSetUnavailable;

                    // Validate custom fields for personal products
                    const isCustomFieldsValid = !isPersonalProduct || (
                      isCustomIllustrationProduct
                        ? (!requiresReferenceImages || customImages.length > 0)
                        : isPetProduct
                        ? ((isPersonalizovaniPrivjesci ? selectedBeadColors.length > 0 : selectedColor !== '') && customImages.length > 0)
                        : (customDescription.trim() !== '' && customDate !== '' && selectedColor !== '')
                    );

                    // Validate custom option text input / image upload (any group's selected option may require it)
                    const coGroupsForValidation = getCustomOptionGroups(product.custom_options);
                    let selectedOptDataWithInput = null;
                    let selectedOptDataWithImageUpload = null;
                    coGroupsForValidation.forEach(group => {
                      const selLabel = selectedCustomOptions[group.customHandle];
                      const opt = (group.options || []).find(o => o.label === selLabel);
                      if (opt?.customInput?.enabled) selectedOptDataWithInput = opt;
                      if (opt?.customImageUpload?.enabled) selectedOptDataWithImageUpload = opt;
                    });
                    const isOptionInputValid = !selectedOptDataWithInput?.customInput?.enabled || customOptionInputText.trim().length > 0;
                    const isOptionImageUploadValid = !selectedOptDataWithImageUpload?.customImageUpload?.enabled
                      || selectedOptDataWithImageUpload.customImageUpload.required === false
                      || customOptionImages.length > 0;

                    // Required standalone text fields must be filled
                    const areRequiredTextFieldsValid = coGroupsForValidation.every(group =>
                      group.type !== 'text' || !group.required || (selectedCustomOptions[group.customHandle] || '').trim().length > 0
                    );

                    const isAddToCartDisabled = isButtonDisabled || (isPersonalProduct && !isCustomFieldsValid) || !isOptionInputValid || !isOptionImageUploadValid || !areRequiredTextFieldsValid;
                    
                    // Helper function to convert already-compressed File to base64
                    const fileToBase64 = (file) => {
                      return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = (error) => reject(error);
                        reader.readAsDataURL(file);
                      });
                    };
                    
                    return (
                      <>
                        <button
                          onClick={async () => {
                            try {
                              const hasOptionInput = selectedOptDataWithInput?.customInput?.enabled && customOptionInputText.trim();
                              const hasOptionImageUpload = selectedOptDataWithImageUpload?.customImageUpload?.enabled && customOptionImages.length > 0;
                              const customData = isPersonalProduct ? {
                                description: customDescription,
                                date: customDate,
                                color: isPersonalizovaniPrivjesci ? selectedBeadColors.join(', ') : selectedColor,
                                accessories: selectedAccessories,
                                secondPet: hasReferenceImages ? secondPet : false,
                                ...(isCustomIllustrationProduct ? { petCount } : {}),
                                firstAccessoryFree: isPersonalizovaniPrivjesci,
                                priceProfile: secondPetProfile,
                                customAccessories: allowsCustomAccessories ? getValidCustomAccessories(customAccessories) : [],
                                images: await Promise.all(customImages.map(file => fileToBase64(file)))
                              } : (hasOptionInput || hasOptionImageUpload) ? {
                                ...(hasOptionInput ? { optionInputText: customOptionInputText.trim() } : {}),
                                ...(hasOptionImageUpload ? { images: await Promise.all(customOptionImages.map(file => fileToBase64(file))) } : {})
                              } : null;
                              addToCart(product.id, currentQuantity, serializeCustomOptionSelections(product.custom_options, selectedCustomOptions), customData, selectedSet);
                              setAddedToCart(true);
                              setTimeout(() => setAddedToCart(false), 2000);
                              if (isPersonalProduct) {
                                setCustomDescription('');
                                setCustomDate('');
                                setCustomImages([]);
                                setSelectedColor('');
                                setSelectedBeadColors([]);
                                setSelectedAccessories([]);
                                setCustomAccessories([]);
                                setSecondPet(false);
                                setPetCount(1);
                              }
                              if (hasOptionInput) {
                                setCustomOptionInputText('');
                              }
                              if (hasOptionImageUpload) {
                                setCustomOptionImages([]);
                              }
                            } catch (error) {
                              console.error('Error converting images:', error);
                              alert(translate('Error processing images. Please try again.'));
                            }
                          }}
                          disabled={isAddToCartDisabled}
                          className={`w-full px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                            isAddToCartDisabled
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-darkerSkin text-white hover:bg-[#d4a76a] hover:shadow-lg'
                          }`}
                        >
                          {isAddToCartDisabled
                            ? (isPersonalProduct && !isCustomFieldsValid
                                ? translate("Please fill in all required fields")
                                : !isOptionInputValid
                                  ? translate("Please enter text")
                                  : !isOptionImageUploadValid
                                    ? translate("Please add an image")
                                    : translate("Out of stock"))
                            : (addedToCart ? translate("Added ✓") : translate("Add to cart"))
                          }
                        </button>
                      </>
                    );
                  })()}
                </div>

                {/* Product Description - hidden for personal products since it's shown under price */}
                {!isPersonalProduct && (
                  <div className="prose prose-lg text-gray-600 mt-8">
                    {localized(product, 'description', country).split('\n').map((line, index) => (
                      <p key={index} className="mb-4">{line}</p>
                    ))}
                  </div>
                )}

                {/* Collapsible Information Sections */}
                <div className="mt-8 space-y-4 border-t pt-6">
                  {/* Product Information */}
                  <div className="border-b border-gray-200">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'info' ? null : 'info')}
                      className="w-full flex items-center justify-between py-4 text-left hover:text-brownSkin transition-colors duration-200"
                    >
                      <span className="font-medium text-gray-900">{translate("Product information")}</span>
                      <svg
                        className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                          expandedSection === 'info' ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        expandedSection === 'info' ? 'max-h-96 pb-4' : 'max-h-0'
                      }`}
                    >
                      <div className="text-gray-600 space-y-4 text-sm">
                        {product.custom_product_info ? (
                          <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.custom_product_info) }} />
                        ) : (
                          getDefaultProductInfo()
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Delivery Information */}
                  <div className="border-b border-gray-200">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'delivery' ? null : 'delivery')}
                      className="w-full flex items-center justify-between py-4 text-left hover:text-brownSkin transition-colors duration-200"
                    >
                      <span className="font-medium text-gray-900">{translate("Delivery information")}</span>
                      <svg
                        className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                          expandedSection === 'delivery' ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        expandedSection === 'delivery' ? 'max-h-96 pb-4' : 'max-h-0'
                      }`}
                    >
                      <div className="text-gray-600 space-y-4 text-sm">
                        {product.custom_delivery_info ? (
                          <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.custom_delivery_info) }} />
                        ) : (
                          getDefaultDeliveryInfo()
                        )}
                      </div>
                    </div>
                  </div>
                </div>
            </div>
            ) : (
              <div className="space-y-8">
                <button 
                  onClick={() => { setShowModal(true); setNotifyProductId(product.id); }} 
                  className="w-full bg-red-100 text-red-600 px-6 py-3 rounded-full font-medium transition-all duration-300 hover:bg-red-200"
                >
                  {translate("Notify me when available")}
                </button>

                {/* Product Description */}
                <div className="prose prose-lg text-gray-600">
                  {localized(product, 'description', country).split('\n').map((line, index) => (
                    <p key={index} className="mb-4">{line}</p>
                  ))}
                </div>

                {/* Collapsible Information Sections */}
                <div className="space-y-4 border-t pt-6">
                  {/* Product Information */}
                  <div className="border-b border-gray-200">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'info' ? null : 'info')}
                      className="w-full flex items-center justify-between py-4 text-left hover:text-brownSkin transition-colors duration-200"
                    >
                      <span className="font-medium text-gray-900">{translate("Product information")}</span>
                      <svg
                        className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                          expandedSection === 'info' ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        expandedSection === 'info' ? 'max-h-96 pb-4' : 'max-h-0'
                      }`}
                    >
                      <div className="text-gray-600 space-y-4 text-sm">
                        {product.custom_product_info ? (
                          <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.custom_product_info) }} />
                        ) : (
                          getDefaultProductInfo()
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Delivery Information */}
                  <div className="border-b border-gray-200">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'delivery' ? null : 'delivery')}
                      className="w-full flex items-center justify-between py-4 text-left hover:text-brownSkin transition-colors duration-200"
                    >
                      <span className="font-medium text-gray-900">{translate("Delivery information")}</span>
                      <svg
                        className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                          expandedSection === 'delivery' ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        expandedSection === 'delivery' ? 'max-h-96 pb-4' : 'max-h-0'
                      }`}
                    >
                      <div className="text-gray-600 space-y-4 text-sm">
                        {product.custom_delivery_info ? (
                          <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.custom_delivery_info) }} />
                        ) : (
                          getDefaultDeliveryInfo()
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

      
          </div>
        </div>
        {/* Happy Pets Section (Personalized keychain + personal mug products) */}
        {isPetProduct && (
          <PetLoveMarquee
            heading={
              isPersonalnaLjubimacSoljica
                ? translate("Over 100+ happy pets already have their mug.")
                : translate("Over 100+ happy pets already have their pendant.")
            }
            altLabel={translate("Bulka Ceramics customer pet")}
          />
        )}

        {/* Packaging Section */}
        <div className="w-full bg-white py-16 px-4 sm:px-8 lg:px-16">
          <div className="max-w-[1500px] mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

              {/* Left — text */}
              <div className="flex-1 space-y-8">
                <div>
                  <span className="inline-block text-xs font-medium tracking-widest text-brownSkin/50 uppercase mb-3 border border-brownSkin/20 px-3 py-1 rounded-full">
                    {translate("Packaging")}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-brownSkin leading-tight">
                  {translate("The box our little plates and mugs arrive in.")}
                  </h2>
                </div>

                <blockquote className="relative pl-5 border-l-2 border-brownSkin/30">
                  <p className="text-brownSkin/80 text-lg leading-relaxed italic">
                    "{translate("This is the kind of box you treasure just as much as what's inside. 😍 Pure magic! ✨ I would never have the heart to throw it away. 🙈🥰")}"
                  </p>
                  <footer className="mt-3 text-sm text-brownSkin/40 not-italic">— {translate("customer comment")}</footer>
                </blockquote>
              </div>

              {/* Right — box image */}
              <div className="flex-1 flex justify-center lg:justify-end">
                <div className="w-full  sm:max-w-[420px] lg:max-w-[550px]">
                  <img
                    src={require('../images/bulkaKutije.png')}
                    alt="Bulka Ceramics pakovanje"
                    className="w-full h-auto object-contain"
                    loading="eager"
                    fetchpriority="high"
                  />
                </div>
              </div>
        

            </div>
          </div>
        </div>

        {product.promo_image && (
          <div className="w-full mt-0">
            <CustomImage src={product.promo_image} alt={product.imageAlt} className="w-full h-full object-cover" loading="eager" />
          </div>
        )}

      </div>



      {/* You May Also Like Section */}
      {!relatedLoading && relatedProducts.length > 0 && (
        <div className="bg-white py-16 border-t border-gray-100">
          <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
             
              <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-4">
                {translate("You may also like")}
              </h2>
              <div className="w-24 h-1 bg-[#facb8c] rounded-full mx-auto"></div>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8">
              {relatedProducts.map(relatedProduct => {
                const category = categories.find(cat => cat.id === relatedProduct.category);
                
                return (
                  <div key={relatedProduct.id} className="group">
                    <Link to={`/proizvod/${category?.path || 'default'}/${relatedProduct.path}`} className="block">
                      <div className="relative ">
                        {/* Sale Badge */}
                        {relatedProduct.is_on_sale && relatedProduct.sale_discount_percentage > 0 && (
                          <div className="absolute top-2 right-2 z-10">
                            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                              -{relatedProduct.sale_discount_percentage}%
                            </span>
                          </div>
                        )}
                        
                        <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden">
                          <CustomImage 
                            src={relatedProduct.imageUrl} 
                            alt={relatedProduct.imageAlt} 
                            className="h-full w-full object-cover rounded-xl transition-transform duration-300 group-hover:scale-105" 
                          />
                        </div>
                        
                        <div className="p-2">
                          <div>
                            <Link to={`/proizvod/${category?.path || 'default'}/${relatedProduct.path}`} className="no-underline text-brownSkin inline-block">
                              <h3 className="text-base font-medium leading-tight mb-1 text-brownSkin hover:text-brownSkin/80 transition-colors duration-200">
                                {localized(relatedProduct, 'name', country)}
                              </h3>
                            </Link>
                            <div className="product-card__price-badge">
                              <div className="product-price">
                                {relatedProduct.is_on_sale && relatedProduct.sale_discount_percentage > 0 ? (
                                  <div className="flex flex-col">
                                    <div className="flex items-center gap-2">
                                      <span className="text-brownSkin font-medium">
                                        {Number(relatedProduct[`discounted_${currency}`] || 0).toFixed(2)} {currency}
                                      </span>
                                      <span className="text-sm text-gray-500 line-through">
                                        {Number(relatedProduct[currency] || 0).toFixed(2)} {currency}
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <span className="text-brownSkin font-medium">
                                      {Number(relatedProduct[currency] || 0).toFixed(2)} {currency}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>

            {/* View the entire summer (main) collection CTA */}
            {(() => {
              const summerCollection =
                categories?.find(cat => cat.is_main_collection === true || cat.is_main_collection === 1) ||
                categories?.find(cat =>
                  (cat.is_collection === true || cat.is_collection === 1) &&
                  (cat.is_collection_listed === true || cat.is_collection_listed === 1)
                );
              const summerUrl = summerCollection?.collection_url || summerCollection?.path;
              if (!summerCollection || !summerUrl) return null;
              return (
                <div className="text-center mt-12">
                  <Link
                    to={`/kolekcije/${summerUrl}`}
                    className="inline-flex items-center gap-2 bg-darkerSkin text-brownSkin px-8 py-3 rounded-full text-lg font-medium hover:bg-brownSkin hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    {translate("View the entire summer collection")}
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                    </svg>
                  </Link>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <Footer categories={categories} />
    </div>
  );
}

export default ItemDetail;
